import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, Download, CheckCircle2, XCircle } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

export const ImportAssetsModal = ({ open, onOpenChange, token, onImported }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
  };

  const handleClose = (isOpen) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/assets/import/template`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "blue_element_asset_import_template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/assets/import`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} asset(s)`);
        onImported();
      } else {
        toast.error("No assets were imported");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl" data-testid="import-assets-modal">
        <DialogHeader>
          <DialogTitle className="text-white">Import Assets from CSV</DialogTitle>
          <DialogDescription className="text-slate-400">
            Required columns: name, asset_type (land/residential/commercial), state, lga, address. Optional: ownership_type, estimated_value, description, gps_coordinates, plot_number, unit_number, block_number.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 py-2">
            <Button variant="outline" className="btn-secondary" onClick={downloadTemplate} data-testid="download-import-template-btn">
              <Download className="w-4 h-4 mr-2" /> Download CSV template
            </Button>
            <div className="space-y-2">
              <Label className="text-slate-300">CSV file</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="input-default file:text-slate-300 file:bg-slate-800 file:border-0 file:rounded file:px-2 file:mr-3"
                data-testid="import-file-input"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="import-result">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-slate-400 text-sm">Imported</p>
                  <p className="text-2xl font-bold text-emerald-400" data-testid="import-success-count">{result.imported}</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                <XCircle className="w-8 h-8 text-red-400" />
                <div>
                  <p className="text-slate-400 text-sm">Skipped</p>
                  <p className="text-2xl font-bold text-red-400" data-testid="import-failed-count">{result.failed}</p>
                </div>
              </div>
            </div>
            {result.errors.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-xl border border-slate-700" data-testid="import-error-table">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800 text-slate-400 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Row</th>
                      <th className="text-left px-3 py-2 font-medium">Name</th>
                      <th className="text-left px-3 py-2 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((err) => (
                      <tr key={err.row} className="border-t border-slate-800 text-slate-300" data-testid={`import-error-row-${err.row}`}>
                        <td className="px-3 py-2 mono">{err.row}</td>
                        <td className="px-3 py-2">{err.name}</td>
                        <td className="px-3 py-2 text-red-300">{err.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button variant="outline" className="btn-secondary" onClick={reset} data-testid="import-another-btn">Import another file</Button>
              <Button className="btn-primary" onClick={() => handleClose(false)} data-testid="import-done-btn">Done</Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="btn-secondary" onClick={() => handleClose(false)} data-testid="import-cancel-btn">Cancel</Button>
              <Button className="btn-primary" disabled={!file || uploading} onClick={handleImport} data-testid="import-submit-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Import
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
